import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TraceData, TraceDataKind, TraceDataRef } from '../../api/trace';
import { TraceDataView } from './TraceDataView';

/** Which of the two stores a button reaches into, so the pair stays apart on sight. */
const KIND_ICON: Record<TraceDataKind, IconProp> = {
    InboundEvent: 'inbox',
    Payload: 'file-code',
};

/** A stable key per pointer, so two on one row can be open independently. */
const refKey = (
    owner: string,
    reference: TraceDataRef,
    index: number
): string =>
    `${owner}|${index}|${reference.kind}|${reference.objectGuid ?? ''}${
        reference.eventId ?? ''
    }`;

export interface ITraceDataRefsProps {
    /** The step or the row these belong to, so the open state cannot collide. */
    owner: string;

    refs: TraceDataRef[];

    /** Fetches one stored body, when somebody opens it. */
    read: (reference: TraceDataRef) => Promise<TraceData>;
}

/**
 * The buttons that open a stored body, and the bodies that are open.
 *
 * A button rather than an always-open panel: a station can point at a body of tens of
 * kilobytes, and a chain with three of those unfolded is no longer a chain you can read.
 *
 * Its own file because both places that show what an object was made of need it - the steps
 * that store a body, and the input and output rows that name one - and two copies of this
 * would be two ways of opening the same table.
 */
export const TraceDataRefs = (props: ITraceDataRefsProps) => {
    const [open, setOpen] = useState<Set<string>>(new Set());

    const toggle = (key: string) =>
        setOpen((current) => {
            const next = new Set(current);

            if (!next.delete(key)) {
                next.add(key);
            }

            return next;
        });

    if (props.refs.length === 0) {
        return null;
    }

    return (
        <div className="trace-step__data">
            <div className="trace-step__data-buttons">
                {props.refs.map((reference, index) => {
                    const key = refKey(props.owner, reference, index);

                    return (
                        <button
                            type="button"
                            key={key}
                            className={
                                open.has(key)
                                    ? 'trace-data-button trace-data-button--open'
                                    : 'trace-data-button'
                            }
                            onClick={() => toggle(key)}
                            aria-expanded={open.has(key)}
                            title={reference.hint ?? undefined}
                        >
                            <FontAwesomeIcon
                                icon={KIND_ICON[reference.kind]}
                                className="trace-data-button__icon"
                            />
                            {reference.label}
                            <FontAwesomeIcon
                                icon={open.has(key) ? 'angle-up' : 'angle-down'}
                                className="trace-data-button__chevron"
                            />
                        </button>
                    );
                })}
            </div>

            {props.refs
                .map((reference, index) => ({
                    reference,
                    key: refKey(props.owner, reference, index),
                }))
                .filter((entry) => open.has(entry.key))
                .map((entry) => (
                    <TraceDataView
                        key={entry.key}
                        reference={entry.reference}
                        read={props.read}
                    />
                ))}
        </div>
    );
};
